import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
} from '@mui/material';

import {
    CheckCircleOutline,
    LocalShipping,
    AttachMoney,
    VerifiedUser,
    Autorenew,
    Home,
    DeleteOutline,
    StarBorder,
    ShoppingCart,
    Undo,
    BuildCircle,
    ArrowForwardIos,
    Apartment,
    MenuBook,
    AssignmentInd,
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェアと家具家電レンタルの新常識' },
    { id: 'comparison', label: '主要レンタルサービス比較' },
    { id: 'cost', label: '購入とレンタルのコストを徹底比較' },
    { id: 'recommend', label: 'おすすめのレンタルプランと選び方' },
    { id: 'delivery', label: '配送・設置・メンテナンスの実態' },
    { id: 'moveout', label: '退去時の注意点とトラブル回避法' },
    { id: 'summary', label: 'まとめ：賢く使って快適ルームシェア' },
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.15rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアと家具家電レンタルの新常識
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        ルームシェアを始めるとき、「初期費用をいかに抑えるか」は誰もが悩むポイント。特に家具や家電は、人数分揃えたり、使い終わった後の処分も一苦労です。そんな悩みをスマートに解決するのが「家具家電レンタルサービス」。<b>手間なく、費用も抑えて、快適な共同生活</b>を実現できます。本記事では、最新のレンタルサービスを徹底比較し、コスト・サービス・選び方から退去時の注意点まで、ルームシェア経験者の視点で詳しく解説します。
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>

                </Box>
                <Typography variant="body2" color="textSecondary">
                    この記事は2025年時点のサービス情報をもとに作成しています。最新のプランや価格は各社公式サイトをご確認ください。他にも様々なサービスがありますので、必要に応じ比較検討ください。
                </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 比較セクション */}
            <Box id="comparison" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    主要レンタルサービス比較
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家具家電レンタルサービスは年々進化し、<b>ルームシェアにぴったりなプランやオプション</b>も充実しています。ここでは、主要サービスの特徴を分かりやすく一覧でご紹介します。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {/* 各サービスのカード */}
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 400, mx: 'auto', borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Apartment sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography variant="h6">
                                        CLAS（クラス）
                                    </Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><StarBorder color="primary" /></ListItemIcon>
                                        <ListItemText primary="月額440円〜" secondary="最低利用期間なし" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                        <ListItemText primary="家具・家電・雑貨をワンストップ" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><LocalShipping color="action" /></ListItemIcon>
                                        <ListItemText primary="配送料一律1,650円（返却時も同額）" />
                                    </ListItem>
                                </List>
                                <Chip label="都市部中心" color="primary" variant="outlined" sx={{ mr: 1, mb: 1 }} />
                                <Chip label="短期利用◎" color="success" variant="outlined" sx={{ mb: 1 }} />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 400, mx: 'auto', borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <ShoppingCart sx={{ mr: 1, color: 'secondary.main' }} />
                                    <Typography variant="h6">
                                        かして！どっとこむ
                                    </Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><StarBorder color="primary" /></ListItemIcon>
                                        <ListItemText primary="セット割引あり" secondary="2年目以降は割安" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><LocalShipping color="action" /></ListItemIcon>
                                        <ListItemText primary="全国配送・設置対応" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><VerifiedUser color="info" /></ListItemIcon>
                                        <ListItemText primary="新品・中古選択可" />
                                    </ListItem>
                                </List>
                                <Chip label="地方もOK" color="info" variant="outlined" sx={{ mr: 1, mb: 1 }} />
                                <Chip label="長期利用向き" color="primary" variant="outlined" sx={{ mb: 1 }} />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 400, mx: 'auto', borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Home sx={{ mr: 1, color: 'success.main' }} />
                                    <Typography variant="h6">
                                        subsclife（サブスクライフ）
                                    </Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><AttachMoney color="success" /></ListItemIcon>
                                        <ListItemText primary="月額500円〜" secondary="最低3カ月〜" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><VerifiedUser color="primary" /></ListItemIcon>
                                        <ListItemText primary="新品中心・デザイン性高" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><Autorenew color="action" /></ListItemIcon>
                                        <ListItemText primary="途中買取可" />
                                    </ListItem>
                                </List>
                                <Chip label="デザイン重視" color="secondary" variant="outlined" sx={{ mr: 1, mb: 1 }} />
                                <Chip label="短中期◎" color="success" variant="outlined" sx={{ mb: 1 }} />
                            </CardContent>
                        </Card>
                    </Grid>

                </Grid>
                <Typography variant="body2" color="textSecondary">
                    ※サービス内容・料金は2024年6月時点。詳細・最新情報は各公式サイトでご確認ください。
                </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* コスト比較 */}
            <Box id="cost" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    購入とレンタルのコストを徹底比較
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアで悩みがちな「初期投資」。<b>購入とレンタル、どちらが本当にお得なのか？</b>具体的な数値で比較してみましょう。
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <AttachMoney sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                        例：3人暮らしの初期費用比較（リビング用家具家電セット）
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>購入の場合</Typography>
                                <List dense>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon><ShoppingCart color="primary" /></ListItemIcon>
                                        <ListItemText primary="ソファ・テーブル・TV・冷蔵庫・洗濯機など" />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon><ArrowForwardIos color="disabled" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ fontWeight: 'bold', color: 'error.main', fontSize: '1.2rem' }}>
                                                    約18万円〜30万円
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                </List>
                                <Typography variant="caption" color="textSecondary">
                                    ※人数・品質・中古/新品によって変動
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>レンタルの場合</Typography>
                                <List dense>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon><Autorenew color="success" /></ListItemIcon>
                                        <ListItemText primary="同等セットを月額5,000円〜12,000円で利用可" />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon><ArrowForwardIos color="disabled" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ fontWeight: 'bold', color: 'success.main', fontSize: '1.2rem' }}>
                                                    初期費用 0円〜1万円前後
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                </List>
                                <Typography variant="caption" color="textSecondary">
                                    ※月額×利用期間＋配送料が基本。複数人で割ればさらに負担減
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
                <Box sx={{ mb: 2 }}>

                </Box>
                <Typography variant="body2" color="textSecondary">
                    <b>POINT:</b> レンタルは「初期費用を劇的に抑えたい」「短〜中期の滞在」「処分コスト・手間を減らしたい」人に特におすすめです。
                </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* おすすめプラン */}
            <Box id="recommend" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    おすすめのレンタルプランと選び方
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア向けレンタル選びのコツは、<b>「シェアするもの」「個人で使うもの」を分けて賢く組み合わせる</b>こと。以下にタイプ別おすすめプランをまとめました。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <MenuBook sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>共有スペース用</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="大型冷蔵庫・洗濯機・電子レンジ" secondary="同居人数で割って負担減" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="ソファ・ダイニングセット・テレビ" secondary="暮らしの質UP" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="定額セットプランが安くて便利" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <MenuBook sx={{ color: 'secondary.main', mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>個人スペース用</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="ベッド・デスク・椅子" secondary="各自の好みに合わせて選べる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="小型家電（照明・加湿器・収納など）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="短期利用や転勤族にも◎" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                        <b>専門家アドバイス：</b>ベースはレンタル、こだわり部分だけ購入・持ち込みが効率的。個人用はコンパクト家電やサブスク型も活用を。
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 配送・設置・メンテ */}
            <Box id="delivery" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    配送・設置・メンテナンスの実態
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    レンタルの最大の魅力は、<b>「重い家具・家電を運ばずに済む」</b>こと。設置やメンテナンス対応もサービス選びの重要ポイントです。
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LocalShipping sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>配送・設置</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="指定日配送OK" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="設置・組立もお任せ" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="段ボールや梱包材の回収あり" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Autorenew sx={{ color: 'success.main', mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>メンテナンス</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="初期不良・故障は無償交換対応" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="日常使いの汚れは問題なし" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="クリーニング済みで安心" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>

                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    <b>注意:</b> 配送エリアや設置方法はサービスごとに異なります。エレベーターなし物件や大型家具は追加料金が発生することもあるので、事前確認を。
                </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 退去時の注意点 */}
            <Box id="moveout" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    退去時の注意点とトラブル回避法
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア解散・引越し時は<b>「レンタル家具家電の返却手続き」</b>がとても重要。スムーズな退去のためのポイントをまとめました。
                </Typography>
                <List sx={{ mb: 1 }}>
                    <ListItem>
                        <ListItemIcon><Undo color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="返却日・立会いの調整は早めに"
                            secondary="特に複数人名義の場合は代表者がまとめて手配"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><DeleteOutline color="error" /></ListItemIcon>
                        <ListItemText
                            primary="破損・著しい汚れは費用発生の可能性"
                            secondary="通常使用による小傷・汚れはほとんど問題なし"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><AttachMoney color="success" /></ListItemIcon>
                        <ListItemText
                            primary="返却送料や回収費用も要確認"
                            secondary="サービスによって送料負担が異なる"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><VerifiedUser color="info" /></ListItemIcon>
                        <ListItemText
                            primary="名義や支払方法の統一管理が安心"
                            secondary="トラブル防止のためLINEグループなどで共有を"
                        />
                    </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>

                </Box>
                <Typography variant="body2" color="textSecondary">
                    <b>退去時アドバイス：</b>
                    代表者への返却連絡・分担金精算もお忘れなく。写真記録を残しておくと万が一の証明にもなります。
                </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ：賢く使って快適ルームシェア
                </Typography>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 3, bgcolor: '#f9fbe7', mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        家具家電レンタルサービスは、<b>ルームシェアの初期費用・手間・ストレスを格段に減らす</b>、現代の新定番。用途や人数、期間に合わせて選ぶことで、より自由でスマートな共同生活が実現します。
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="初期費用を抑えて、必要な分だけ賢くレンタル" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="配送・設置・回収もプロにお任せ" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="退去時の処分リスク・コストも最小限" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" color="textSecondary">
                    ルームシェアの新生活は、<b>「レンタル」</b>を賢く活用して、快適で自由なスタートを切りましょう！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-furniture-appliance-rental-comparison',
    slug: 'roomshare-furniture-appliance-rental-comparison',
    title: 'ルームシェア向け家具家電レンタルサービス比較：初期費用を抑える賢い選択',
    excerpt: 'レンタルサービス比較\n購入とレンタルのコスト\nおすすめプラン\n配送・設置・メンテ\n退去時の注意点',
    coverImage: '/images/blog/cover-roomshare-rental_roomshare-furniture-appliance-rental-comparison.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。ナチュラルな家具と観葉植物、温かみのある照明が調和した空間。',
    publishedAt: '2024-06-18T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '家具家電レンタル', '暮らし'],
    tags: ['ルームシェア', '家具レンタル', '家電レンタル', '初期費用', '引越し'],
    author: {
        name: '鈴木 直人',
        avatar: '/images/avatars/suzuki-naoto.jpg',
        bio: 'ルームシェア歴10年。東京・関西での共同生活やサブスクリプションサービスの活用に精通。シェアハウス運営・コンサルティングも行う暮らしの実践者。',
        profilePicture: '/images/avatars/suzuki-naoto.jpg'
    },
    viewCount: 1243,
    contentComponent: PostContent
};

export default post;